import { useState, useEffect } from 'react';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Autocomplete, Chip, Divider, FormControl, Grid, InputLabel, MenuItem, Select, Typography, createFilterOptions } from '@mui/material';

import { MuiColorInput } from 'mui-color-input'
import { Panels } from '../config/Panels';
import { useAuth } from '../context/AuthContext';
import { logEventAnalytics } from '../lib/firebase/analytics';
import { createUserAnalysis, getUserAnalysisData } from '../lib/dataService';
import { logEvent } from '../lib/firebase/logs';

export default function NewAnalysisDialog({
  newAnalysisDialogOpen,
  setNewAnalysisDialogOpen,
  userUid,
  analises
}) {

  const { permission } = useAuth();
  const [title, setTitle] = useState('');
  const [selectedPanel, setSelectedPanel] = useState('');
  const [selectedAnalysis, setSelectedAnalysis] = useState('');
  const [teamNames, setTeamNames] = useState(['Mandante', 'Visitante']);
  const [colors, setColors] = useState(['#095FA3', '#C20000']);
  const [allPanels, setAllPanels] = useState([]);
  const [customPanelOptions, setCustomPanelOptions] = useState([]);
  const [customPanelLeftOptions, setCustomPanelLeftOptions] = useState([]);
  const [customPanelRightOptions, setCustomPanelRightOptions] = useState([]);
  const [editTitle, setEditTitle] = useState(false);

  const filter = createFilterOptions();

  const handleChangeColor = (index, color) => {
    setColors(prevColors => {
      const newColors = [...prevColors];
      newColors[index] = color;
      return newColors;
    });
  };

  const handleClose = () => {
    setNewAnalysisDialogOpen(false);
  };

  const handleConfirm = async () => {

    const currentDate = new Date().toISOString();

    const analysis = {
      capa: "/images/capa-template.jpg",
      data: currentDate,
      lastUpdate: currentDate,
      title: title,
      panel: Panels[selectedPanel] ? Panels[selectedPanel].panel : "Painel Customizado",
    }

    let analysisData = Panels[selectedPanel] ? Panels[selectedPanel] : Panels[3];
    analysisData.title = title;
    analysisData.customCutPoints = true;

    if (selectedPanel === 0 || selectedPanel === 2 || selectedPanel === 3 || selectedPanel === 99) {
      //Painel de edição de vídeo [1] e Motion [4] não precisam os painéis laterais
      analysisData.leftActions[0].title = teamNames[0];
      analysisData.leftActions[0].colors[0] = colors[0];
      analysisData.leftActions[0].colors[1] = colors[1];
      analysisData.rightActions[0].title = teamNames[1];
      analysisData.rightActions[0].colors[0] = colors[1];
      analysisData.rightActions[0].colors[1] = colors[0];
    }

    //Painel Customizado precisamos gerar os grupo de ações
    if (selectedPanel === 3 || selectedPanel === 99) {
      //Grupo de Ações da Esquerda
      customPanelLeftOptions.forEach((group) => {
        analysisData.leftActions[0].actions.push(
          {
            title: group.inputValue ? group.inputValue : group.title,
            button: "Adicionar",
            hasOptions: true,
            hasSlider: true,
            multipleOptions: true,
            addOptions: true,
            customBullets: false,
            analytics: 'donut',
            options: group.options ? group.options : [],
            data: []
          });
      });

      //Grupo de Ações da Direita
      customPanelRightOptions.forEach((group) => {
        analysisData.rightActions[0].actions.push(
          {
            title: group.inputValue ? group.inputValue : group.title,
            button: "Adicionar",
            hasOptions: true,
            hasSlider: true,
            multipleOptions: true,
            addOptions: true,
            customBullets: false,
            analytics: 'donut',
            options: group.options ? group.options : [],
            data: []
          });
      });

    }

    const analiseUid = await createUserAnalysis(userUid, analysis, analysisData);

    if (!analiseUid) {
      logEvent("create_analysis_error", { message: "Erro ao criar nova análise, da caixa de diálogo" });
      console.error("Erro ao criar análise. Verifique os logs.");
      return;
    }

    logEventAnalytics("new_analysis_created",
      {
        panel: selectedPanel,
        subscriber: permission,
        app_platform: window.electron ? "electron" : "web"
      }
    );

    //abrir página da análise - motion tem uma vista própria
    if (selectedPanel === 4) {
      window.location.href = `/motion/${analiseUid}`;
    } else {
      window.location.href = `/analisar/${analiseUid}`;
    }

  }

  const handleOptionsChange = (e, newValue, side) => {

    if (!newValue) {
      if (side === "left") {
        setCustomPanelLeftOptions([]);
      } else {
        setCustomPanelRightOptions([]);
      }
      return;
    }

    const processedValue = newValue.map(option => {
      if (typeof option === 'string') {
        return { title: option };
      }
      if (option && option.inputValue) {
        return { title: option.inputValue };
      }
      return option;
    });

    const validNewValue = processedValue.filter(newOption =>
      typeof newOption === 'object' && newOption !== null && !Array.isArray(newOption));

    setCustomPanelOptions(prevOptions => {
      const newOptionsToAdd = validNewValue.filter(newOption =>
        !prevOptions.some(option => option.title === newOption.title));
      return [...prevOptions, ...newOptionsToAdd];
    });

    if (side === "left") {
      setCustomPanelLeftOptions(validNewValue);
    } else {
      setCustomPanelRightOptions(validNewValue);
    }
  }

  const handleSelectedAnalysisChange = async (e) => {
    const analysisUid = e.target.value;
    try {
      const analysisData = await getUserAnalysisData(userUid, analysisUid);
      if (analysisData) {
        setTeamNames([analysisData.leftActions[0].title, analysisData.rightActions[0].title]);
        setCustomPanelLeftOptions(analysisData.leftActions[0].actions);
        handleOptionsChange(null, analysisData.leftActions[0].actions, "left");

        setCustomPanelRightOptions(analysisData.rightActions[0].actions);
        handleOptionsChange(null, analysisData.rightActions[0].actions, "right");

        setColors([analysisData.leftActions[0].colors[0], analysisData.leftActions[0].colors[1]]);
        setSelectedAnalysis(analysisUid);
      }

    } catch (error) {
      console.error("Erro ao buscar análise:", error);
    }
  };

  useEffect(() => {
    setTitle('');
    setSelectedPanel('');
    setTeamNames(['Mandante', 'Visitante']);
    setColors(['#095FA3', '#C20000']);
    setAllPanels(Panels);
    setEditTitle(false);
  }, [newAnalysisDialogOpen]);

  return (
    <>
      <Dialog open={newAnalysisDialogOpen} onClose={handleClose}>
        <DialogTitle>Nova Análise</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Preencha as informações para iniciar uma nova análise.
          </DialogContentText>
          <TextField
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (!editTitle) { setEditTitle(true); }
            }}
            margin="normal"
            id="title"
            label="Título da Análise"
            fullWidth
          />
          {editTitle && (
            <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
              <InputLabel>Painel</InputLabel>
              <Select
                id="panel"
                value={selectedPanel}
                onChange={(e) => { setSelectedPanel(e.target.value); }}
                label="Painel"
                data-testid="select-painel"
              >

                <MenuItem value={1} data-testid="option-estatica">
                  Edição de Vídeo (Estática)
                </MenuItem>

                <MenuItem value={4}>
                  Edição de Vídeo (Motion)
                </MenuItem>

                <Divider />

                <MenuItem value={0}>
                  Análise Básica
                </MenuItem>

                <MenuItem value={3} disabled={!permission}>
                  Análise Customizada
                  {!permission && (
                    <Chip sx={{ ml: 1 }} label="PRO" color="primary" size="small" variant="outlined" />
                  )}
                </MenuItem>

                <MenuItem value={2} disabled={!permission}>
                  Análise Completa de Partida
                  {!permission && (
                    <Chip sx={{ ml: 1 }} label="PRO" color="primary" size="small" variant="outlined" />
                  )}
                </MenuItem>

                <Divider />

                <MenuItem value={99} disabled={!permission}>
                  Importar de Outra Análise
                  {!permission && (
                    <Chip sx={{ ml: 1 }} label="PRO" color="primary" size="small" variant="outlined" />
                  )}
                </MenuItem>

              </Select>
            </FormControl>
          )}

          {selectedPanel === 99 && (
            <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
              <InputLabel>Análise</InputLabel>
              <Select
                id="panel"
                value={selectedAnalysis}
                onChange={handleSelectedAnalysisChange}
                label="Análise"
              >

                {analises.map((analise, index) => (
                  analise.panel === 'Painel Customizado' && (
                    <MenuItem key={index} value={analise.uid}>
                      {analise.title}
                    </MenuItem>
                  )
                ))}

              </Select>
            </FormControl>
          )}

          {allPanels[selectedPanel]?.configurationCase === 1 && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    value={teamNames[0]}
                    onChange={(e) => { setTeamNames([e.target.value, teamNames[1]]) }}
                    margin="normal"
                    label="Título do Painel Esquerdo"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <MuiColorInput
                    fullWidth
                    margin="normal"
                    value={colors[0]}
                    onChange={(e) => { handleChangeColor(0, e); }}
                    format="hex"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    value={teamNames[1]}
                    onChange={(e) => { setTeamNames([teamNames[0], e.target.value]) }}
                    margin="normal"
                    label="Título do Painel Direito"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <MuiColorInput
                    fullWidth
                    margin="normal"
                    value={colors[1]}
                    onChange={(e) => { handleChangeColor(1, e); }}
                    format="hex"
                  />
                </Grid>
              </Grid>
            </>
          )}
          {(allPanels[selectedPanel]?.configurationCase === 2 || (selectedPanel === 99 && selectedAnalysis !== '')) && (
            <>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <Typography variant="h5">Título do Painel Esquerdo</Typography>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    value={teamNames[0]}
                    onChange={(e) => { setTeamNames([e.target.value, teamNames[1]]) }}
                    label="Título do Painel Esquerdo"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <MuiColorInput
                    fullWidth
                    value={colors[0]}
                    onChange={(e) => { handleChangeColor(0, e); }}
                    format="hex"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple={true}
                    //value={textFields[groupIndex][actionIndex]}                  
                    freeSolo
                    clearOnBlur={true}
                    options={customPanelOptions}
                    getOptionLabel={(option) => {
                      let isExisting = customPanelOptions.some((optionlabel) => optionlabel.title === option.title);
                      if (isExisting) {
                        return option.title;
                      } else {
                        return option.buttonName;
                      }
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const { inputValue } = params;
                      let isExisting = options.some((option) => inputValue === option.inputValue);
                      if (inputValue !== '' && !isExisting) {
                        filtered.push({
                          inputValue,
                          title: inputValue,
                          buttonName: `Adicionar "${inputValue}"`,
                        });
                      }
                      return filtered;
                    }}
                    onChange={(e, value) => {
                      handleOptionsChange(e, value, "left");
                    }}
                    value={customPanelLeftOptions}
                    renderInput={(params) => <TextField label="Linhas do Tempo" {...params} />}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <Typography variant="h5">Título do Painel Direito</Typography>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    value={teamNames[1]}
                    onChange={(e) => { setTeamNames([teamNames[0], e.target.value]) }}
                    label="Título do Painel Direito"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <MuiColorInput
                    fullWidth
                    value={colors[1]}
                    onChange={(e) => { handleChangeColor(1, e); }}
                    format="hex"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple={true}
                    //value={textFields[groupIndex][actionIndex]}                  
                    freeSolo
                    clearOnBlur={true}
                    options={customPanelOptions}
                    getOptionLabel={(option) => {
                      let isExisting = customPanelOptions.some((optionlabel) => optionlabel.title === option.title);
                      if (isExisting) {
                        return option.title;
                      } else {
                        return option.buttonName;
                      }
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const { inputValue } = params;
                      let isExisting = options.some((option) => inputValue === option.inputValue);
                      if (inputValue !== '' && !isExisting) {
                        filtered.push({
                          inputValue,
                          title: inputValue,
                          buttonName: `Adicionar "${inputValue}"`,
                        });
                      }
                      return filtered;
                    }}
                    onChange={(e, value) => {
                      handleOptionsChange(e, value, "right");
                    }}
                    value={customPanelRightOptions}
                    renderInput={(params) => <TextField label="Linhas do Tempo" {...params} />}
                  />
                </Grid>
              </Grid>
            </>
          )}


        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>Cancelar</Button>
          <Button
            data-testid="btn-confirmar-nova-analise"
            variant="contained"
            onClick={handleConfirm}
            disabled={
              (title === '') ||
              (selectedPanel === '') ||
              (selectedPanel === 0 && (teamNames[0] === '' || teamNames[1] === '')) ||
              (selectedPanel === 2 && (teamNames[0] === '' || teamNames[1] === '')) ||
              (selectedPanel === 3 && (teamNames[0] === '' || teamNames[1] === ''))
            }
          >Confirmar</Button>
        </DialogActions>
      </Dialog>
    </>
  );

}